import { Dispatch } from 'react';

import IconFacebook from 'dibs-icons/exports/legacy/Facebook';
import IconInstagram from 'dibs-icons/exports/legacy/Instagram';
import IconLinkedin from 'dibs-icons/exports/legacy/Linkedin';
import IconHouzz from 'dibs-icons/exports/legacy/Houzz';
import AnchorLink from 'dibs-icons/exports/legacy/AnchorLink';

export type SocialDetails = {
    socialUserName: string;
    socialUrl: string;
    network: SocialNetworkName;
};

export const PREFIX_HTTPS = 'https://' as const;
export const PREFIX_WWW = 'www.' as const;

export const socialNetworksConfig = {
    Facebook: {
        Icon: IconFacebook,
        exampleUrl: 'https://www.facebook.com/1stDibs/',
        baseUrls: ['facebook.com/', 'facebook.com/groups/'],
        // the number of path segments allowed when validating the array
        // FB, IG: ['1stDibs'].length === 1
        // LinkedIn: ['in', '1stDibs'].length === 2
        validSegmentsLength: [1, 2],
    },
    Instagram: {
        Icon: IconInstagram,
        exampleUrl: 'https://www.instagram.com/1stDibs/',
        baseUrls: ['instagram.com/'],
        validSegmentsLength: [1],
    },
    LinkedIn: {
        Icon: IconLinkedin,
        exampleUrl: 'https://www.linkedin.com/in/1stDibs/',
        baseUrls: ['linkedin.com/in/', 'linkedin.com/pro/'],
        validSegmentsLength: [2, 2],
    },
    Houzz: {
        Icon: IconHouzz,
        exampleUrl: 'https://www.houzz.com/user/1stDibs/',
        baseUrls: ['houzz.com/user/', 'houzz.com/pro/'],
        validSegmentsLength: [2, 2],
    },
    Website: {
        Icon: AnchorLink,
        exampleUrl: 'https://yoursite.com',
    },
} as const;

export type SocialNetworkName = keyof typeof socialNetworksConfig;

export const SELECT_NETWORK = 'SELECT_NETWORK' as const;
export const CHANGE_USER_INFO = 'CHANGE_USER_INFO' as const;
export const SAVE_AND_REMOVE_SELECTED_NETWORK = 'SAVE_AND_REMOVE_SELECTED_NETWORK' as const;
export const DELETE_NETWORK = 'DELETE_NETWORK' as const;
export const INPUT_ERROR = 'INPUT_ERROR' as const;

export type ReducerState = {
    socialDetailsList: SocialDetails[] | null;
    selectedNetwork?: SocialNetworkName | null;
    savedNetwork?: SocialNetworkName | null;
    socialUserName?: string | null;
    socialUrl?: string | null;
    inputError?: SocialNetworkName | null;
    errorInput?: string | null;
};

type ReducerActions = {
    type:
        | typeof SELECT_NETWORK
        | typeof CHANGE_USER_INFO
        | typeof SAVE_AND_REMOVE_SELECTED_NETWORK
        | typeof DELETE_NETWORK
        | typeof INPUT_ERROR;
    payload?: {
        selectedNetwork?: SocialNetworkName | null;
        savedNetwork?: SocialNetworkName | null;
        socialUrl?: string | null;
        socialUserName?: string | null;
        inputError?: SocialNetworkName | null;
        errorInput?: string | null;
    };
};

export type ReducerDispatch = Dispatch<ReducerActions>;

export function socialNetworksReducer(state: ReducerState, action: ReducerActions): ReducerState {
    const { type, payload } = action;
    switch (type) {
        case SELECT_NETWORK: {
            const existingNetwork = state?.socialDetailsList?.find(
                i => i.network === payload?.selectedNetwork
            );
            return {
                ...state,
                selectedNetwork: payload?.selectedNetwork,
                socialUrl: existingNetwork?.socialUrl || null,
                socialUserName: existingNetwork?.socialUserName || null,
                savedNetwork: null,
                errorInput: null,
                inputError: null,
            };
        }
        case SAVE_AND_REMOVE_SELECTED_NETWORK: {
            if (payload?.savedNetwork) {
                const existingNetworks = (state?.socialDetailsList || [])?.filter(
                    i => i.network !== payload?.savedNetwork
                );
                const newNetwork: SocialDetails = {
                    network: payload?.savedNetwork,
                    socialUrl: state?.socialUrl || '',
                    socialUserName: state?.socialUserName || '',
                };
                return {
                    ...state,
                    socialDetailsList: [...existingNetworks, newNetwork],
                    selectedNetwork: null,
                };
            }
            return {
                ...state,
                selectedNetwork: null,
            };
        }
        case DELETE_NETWORK:
            return {
                ...state,
                socialDetailsList: (state?.socialDetailsList || []).filter(
                    i => i.network !== payload?.selectedNetwork
                ),
            };
        case INPUT_ERROR:
            return { ...state, errorInput: payload?.errorInput, inputError: payload?.inputError };
        case CHANGE_USER_INFO: {
            if (payload?.socialUserName && payload?.socialUrl) {
                return {
                    ...state,
                    socialUserName: payload?.socialUserName,
                    socialUrl: payload?.socialUrl,
                };
            }
            return state;
        }
        default:
            throw new Error(`missing valid action.type, received ${type}`);
    }
}
